<script lang="ts" setup>
import { MailOutlined, SafetyOutlined } from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const auth = useAuthStore()
const router = useRouter()

const code = ref('')
const loading = ref(false)
const resending = ref(false)
const verified = ref(false)
const authError = ref<string | null>(null)

const email = computed(() => auth.lastRegisteredEmail || auth.user?.email || '')

const handleSubmit = async () => {
  if (!code.value.trim()) {
    authError.value = 'Please enter the verification code.'
    return
  }

  try {
    loading.value = true
    authError.value = null
    await auth.verifyEmail(code.value.trim())
    verified.value = true
    message.success('Email verified!')

    if (auth.isApproved) {
      router.push('/')
    } else {
      router.push('/pending-approval')
    }
  } catch (err) {
    if (err instanceof Error && err.message) {
      authError.value = err.message
    } else {
      authError.value = 'Verification failed. Please try again.'
    }
  } finally {
    loading.value = false
  }
}

const handleResend = async () => {
  if (resending.value) return

  try {
    resending.value = true
    await auth.resendVerification(email.value)
    message.success('Verification code resent!')
  } catch (err) {
    if (err instanceof Error && err.message) {
      authError.value = err.message
    } else {
      authError.value = 'Failed to resend code.'
    }
  } finally {
    resending.value = false
  }
}
</script>
<template>
  <div class="verify-container">
    <a-card class="verify-form" title="Verify Your Email">
      <a-result v-if="verified" status="success" title="Email Verified!">
        <template #extra>
          <a-button type="primary" @click="router.push('/login')">
            Go to Login
          </a-button>
        </template>
      </a-result>

      <template v-else>
        <p style="text-align: center; margin-bottom: 16px">
          <mail-outlined style="font-size: 48px; color: var(--primary-color)" />
        </p>
        <p style="text-align: center; margin-bottom: 24px">
          We sent a verification code to
          <strong>{{ email || 'your email' }}</strong
          >. Enter the code below to verify your account.
        </p>

        <a-form @submit.prevent="handleSubmit">
          <a-form-item
            :validate-status="authError ? 'error' : ''"
            :help="authError"
          >
            <a-input
              v-model:value="code"
              placeholder="Enter verification code"
              size="large"
              style="text-align: center; font-size: 20px; letter-spacing: 8px; font-weight: 600"
              :maxlength="6"
            >
              <template #prefix>
                <SafetyOutlined />
              </template>
            </a-input>
          </a-form-item>

          <a-form-item>
            <a-button
              type="primary"
              :loading="loading"
              :disabled="!code.trim()"
              html-type="submit"
              block
              size="large"
            >
              Verify Email
            </a-button>
          </a-form-item>

          <div style="text-align: center">
            <a-button type="link" :loading="resending" @click="handleResend">
              Resend code
            </a-button>
          </div>

          <a-divider />

          <div style="text-align: center">
            <router-link to="/login">Back to Login</router-link>
          </div>
        </a-form>
      </template>
    </a-card>
  </div>
</template>
<style scoped>
.verify-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 85vh;
}

.verify-form {
  max-width: 420px;
  width: 100%;
}
</style>
